// Resources
import { Organization } from "fhir/r5";
// FHIR
import Client from "fhir-kit-client";

/////////////////////////////////////
//             Client              //
/////////////////////////////////////

const fhirClient = new Client({
  baseUrl: process.env.REACT_APP_FHIR_URL ?? "fhir",
});

function extractOrganizations(bundle: any): Organization[] {
  if (!bundle?.entry || !Array.isArray(bundle.entry)) {
    return [];
  }

  return bundle.entry
    .map((entry: any) => entry?.resource)
    .filter(
      (resource: Organization | undefined) =>
        resource?.resourceType === "Organization"
    );
}

function hasNextPage(bundle: any): boolean {
  return Array.isArray(bundle?.link)
    ? bundle.link.some((link: any) => link?.relation === "next" && link?.url)
    : false;
}

function getOrganizationSearchParameters() {
  return {
    _count: "200",
    _elements: "id,name,identifier,partOf,alias,active,type",
    _sort: "name",
  };
}

function getInternalOrganizationSearchParameters() {
  return {
    ...getOrganizationSearchParameters(),
    _tag: "internal",
  };
}

/**
 * Load one Organization resource from the back.
 *
 * @returns the promise of an Organization.
 */
async function loadOrganization(
  organizationId: string
): Promise<Organization> {
  return fhirClient.read({
    resourceType: "Organization",
    id: organizationId ?? "",
  }) as Promise<Organization>;
}

/**
 * Search and load all Organization resources from the back.
 *
 * @returns the promise of all Organizations.
 */
async function loadAllOrganizations(): Promise<Organization[]> {
  const organizations: Organization[] = [];
  let bundle = (await fhirClient.search({
    resourceType: "Organization",
    searchParams: getOrganizationSearchParameters(),
  })) as any;

  organizations.push(...extractOrganizations(bundle));

  while (hasNextPage(bundle)) {
    bundle = (await fhirClient.nextPage({ bundle })) as any;
    organizations.push(...extractOrganizations(bundle));
  }

  return organizations;
}

/**
 * Search and load all internal Organization resources from the back.
 *
 * @returns the promise of all internal Organizations.
 */
async function loadAllInternalOrganizations(): Promise<Organization[]> {
  const organizations: Organization[] = [];
  let bundle = (await fhirClient.search({
    resourceType: "Organization",
    searchParams: getInternalOrganizationSearchParameters(),
  })) as any;

  organizations.push(...extractOrganizations(bundle));

  while (hasNextPage(bundle)) {
    bundle = (await fhirClient.nextPage({ bundle })) as any;
    organizations.push(...extractOrganizations(bundle));
  }

  return organizations;
}

///////////////////////////////
//         Exports           //
///////////////////////////////

const OrganizationService = {
  loadOrganization,
  loadAllOrganizations,
  loadAllInternalOrganizations,
};

export default OrganizationService;
